import { Injectable } from '@angular/core';
import { AuthenticationService } from './authentication.service';
import { Role } from '@core/models/role';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {

  private currentUser?: any;
  constructor(auth: AuthenticationService) {
    auth.user.subscribe(u => this.currentUser = u);
  }

  canEditOrganizationDetails(org: any): boolean {
    return this.isOwnerOrAdmin(org);
  }

  canViewOrganizationDetails(org: any): boolean {
    if (this.isOwnerOrAdmin(org)) return true;

    // If we have any permissions, we can view the organization
    return Object.values(org.userPermissions).some(p => p === true);
  }

  canDeleteOrganization(org: any) {
    return this.isOwnerOrAdmin(org);
  }

  canViewTeamDetails(org: any): boolean {
    return this.isOwnerOrAdmin(org);
  }

  canViewProjectClusters(project: any): boolean {
    if (this.isOwnerOrAdmin(project)) return true;

    // If we have any permissions, we can view the project
    return project.userPermissions?.canEditClusters || false;
  }

  canAddOrganizationTeam(org: any): boolean {
    return this.isOwnerOrAdmin(org);
  }

  canDeleteOrganizationTeam(org: any): boolean {
    return this.isOwnerOrAdmin(org);
  }

  canAddOrganizationProject(org: any): boolean {
    if (this.isOwnerOrAdmin(org)) return true;

    return !!org.userPermissions?.createAndEditProjects;
  }

  canEditOrganizationTeamDetails(org: any): boolean {
    return this.isOwnerOrAdmin(org);
  }

  canAddMembersToOrganizationTeam(org: any): boolean {
    return this.isOwnerOrAdmin(org);
  }

  canRemoveMembersFromOrganizationTeam(org: any): boolean {
    return this.isOwnerOrAdmin(org);
  }

  canEditOrganizationTeamPermissions(org: any, team?: any): boolean {
    if (team?.name == "Owners") return false; 
    return this.isOwnerOrAdmin(org);
  }

  canViewProjectPosts(project: any): boolean {
    return this.canEditProjectPosts(project);
  }

  canEditProjectPosts(project: any): boolean {
    if (!project) return false;

    if (this.isOwnerOrAdmin(project)) return true;

    if (project.userPermissions?.canEditProjects) return true;

    // TODO: check
    return !!project.userPermissions?.canEditProjectPosts;
  }

  canEditProjectDetails(project: any): boolean {
    if (this.isOwnerOrAdmin(project)) return true;

    return !!project.userPermissions?.canEditProjects;
  }

  canViewProjectDetails(project: any): boolean {
    if (this.isOwnerOrAdmin(project)) return true;

    if (Object.values(project.userPermissions).some(p => p === true)) return true;

    if (project.userPermissions?.createAndEditProjects) return true;

    return false;
  }

  canViewClusterSensorControls(project: any): boolean {
    return this.isOwnerOrAdmin(project);
  }

  canArchiveProjects(org: any): boolean {
    return this.isOwnerOrAdmin(org) || org.userPermissions?.createAndEditProjects;
  }

  canRemoveTeamsFromProjects(org: any): boolean {
    if (this.isOwnerOrAdmin(org)) return true;

    if (org.userPermissions.createAndEditProjects) return true;

    return false;
  }

  canAddTeamsToProjects(org: any): boolean {
    if (this.isOwnerOrAdmin(org)) return true;

    if (org.userPermissions.createAndEditProjects) return true;

    return false;
  }

  canDeleteCluster(org: any): boolean {
    if (org == undefined) return false;

    if (this.isOwnerOrAdmin(org)) return true;

    return org.userPermissions.createAndEditProjects;
  }

  canAddClustersToProjects(org: any): boolean {
    if (org == undefined) return false;
    if (this.isOwnerOrAdmin(org)) return true;

    return org.userPermissions.createAndEditProjects || org.userPermissions.createAndEditClusters;
  }

  canEditClusters(project: any): boolean {
    if (this.isOwnerOrAdmin(project)) return true;

    return project.userPermissions?.canEditClusters;

    return false;
  }

  currentUserIsAdmin() {
    return this.currentUser?.role === Role.Admin;
  }

  private isOwnerOrAdmin(entity: any): boolean {
    return this.currentUserIsAdmin() || entity?.isOwner || false;
  }
}
